import WebApp from '@twa-dev/sdk'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Loader2, ShieldCheck, X } from 'lucide-react'
import {
  isPrivacyVersionCompatible,
  privacyApi,
  PrivacyApiError,
  PrivacyConsentStatus,
} from '../api/privacy'
import { setAnalyticsEnabled } from '../utils/analytics'
import { UserDataNotice } from './UserDataNoticeEnglish'

interface PrivacyContextValue {
  status: PrivacyConsentStatus
  withdraw: () => Promise<void>
  deleteData: () => Promise<void>
}

const PrivacyContext = createContext<PrivacyContextValue | null>(null)

export function usePrivacyConsent() {
  const value = useContext(PrivacyContext)
  if (!value) throw new Error('usePrivacyConsent must be used inside PrivacyConsentGate')
  return value
}

export function PrivacyConsentGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<PrivacyConsentStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [checked, setChecked] = useState(false)
  const [declined, setDeclined] = useState(false)
  const [error, setError] = useState('')

  const loadStatus = async (showLoading = true) => {
    if (showLoading) setLoading(true)
    setError(''); setAnalyticsEnabled(false)
    try {
      const next = await privacyApi.status()
      setStatus(next)
      setAnalyticsEnabled(next.accepted)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      if (showLoading) setLoading(false)
    }
  }

  useEffect(() => {
    void loadStatus()
    const recheckWhenVisible = () => {
      if (document.visibilityState === 'visible') void loadStatus(false)
    }
    window.addEventListener('pageshow', recheckWhenVisible)
    document.addEventListener('visibilitychange', recheckWhenVisible)
    return () => {
      window.removeEventListener('pageshow', recheckWhenVisible)
      document.removeEventListener('visibilitychange', recheckWhenVisible)
      setAnalyticsEnabled(false)
    }
  }, [])

  const agree = async () => {
    if (!checked || submitting) return
    setSubmitting(true); setError('')
    try {
      const next = await privacyApi.accept()
      setStatus(next)
      setAnalyticsEnabled(true)
    } catch (reason) {
      if (reason instanceof PrivacyApiError && reason.code === 'privacy_version_mismatch') {
        await loadStatus(false)
      }
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setSubmitting(false)
    }
  }

  const decline = () => {
    setAnalyticsEnabled(false)
    setDeclined(true)
    try { WebApp.close() } catch { /* Browser fallback remains blocked. */ }
  }

  const actions = useMemo<PrivacyContextValue | null>(() => status ? ({
    status,
    withdraw: async () => {
      setAnalyticsEnabled(false)
      const next = await privacyApi.withdraw()
      sessionStorage.clear()
      setStatus(next)
    },
    deleteData: async () => {
      setAnalyticsEnabled(false)
      await privacyApi.deleteData()
      sessionStorage.clear()
      localStorage.clear()
      setStatus(await privacyApi.status())
    },
  }) : null, [status])

  if (loading) return <GateShell><Loader2 className="h-7 w-7 animate-spin text-blue-600" /><p>Checking service preferences…</p></GateShell>
  if (error && !status) return <GateShell><ShieldCheck className="h-8 w-8 text-blue-600" /><p className="max-w-sm text-center text-sm text-red-600">{error}</p><button type="button" onClick={() => void loadStatus()} className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white">Retry</button></GateShell>
  if (declined) return <GateShell><X className="h-8 w-8 text-gray-500" /><h1 className="text-lg font-bold">You chose not to continue</h1><p className="max-w-sm text-center text-sm text-gray-500">You must confirm the User Service and Data Use Notice before accessing the Mini App.</p><button type="button" onClick={() => setDeclined(false)} className="rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-bold">Review the Notice</button></GateShell>

  if (status && !isPrivacyVersionCompatible(status.current_version)) {
    return (
      <GateShell>
        <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
        <h1 className="text-lg font-bold">The Mini App is updating</h1>
        <p className="max-w-sm text-center text-sm text-gray-500">The User Service and Data Use Notice is being updated. Please reload the Mini App before confirming.</p>
        <button type="button" onClick={() => window.location.reload()} className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white">Reload Mini App</button>
      </GateShell>
    )
  }

  if (!status?.accepted) {
    return (
      <div className="min-h-screen bg-gray-100 px-4 py-8 dark:bg-gray-950">
        <div className="mx-auto max-w-lg rounded-3xl border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-700 dark:bg-gray-900">
          <div className="max-h-[58vh] overflow-y-auto overscroll-contain pr-2"><UserDataNotice /></div>
          <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl bg-gray-50 p-3 text-sm leading-5 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
            <input type="checkbox" checked={checked} onChange={(event) => setChecked(event.target.checked)} className="mt-1 h-4 w-4 rounded border-gray-300" />
            <span>I have read, understood, and accepted the User Service and Data Use Notice, including the described personalization and necessary information handling.</span>
          </label>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          <button type="button" onClick={() => void agree()} disabled={!checked || submitting} className="mt-4 flex w-full items-center justify-center rounded-xl bg-blue-600 py-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40">{submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Confirm and Continue'}</button>
          <button type="button" onClick={decline} className="mt-2 w-full rounded-xl py-3 text-sm font-bold text-gray-500">Decline and Exit</button>
        </div>
      </div>
    )
  }

  return <PrivacyContext.Provider value={actions!}>{children}</PrivacyContext.Provider>
}

function GateShell({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 px-6 text-gray-900 dark:bg-gray-950 dark:text-white">{children}</div>
}
